import React, { useState, useMemo, useCallback } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import VisibilityIcon from '@material-ui/icons/Visibility'
import CloseIcon from '@material-ui/icons/Close'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import WarningIcon from '@material-ui/icons/Warning'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import ScheduleIcon from '@material-ui/icons/Schedule'
import DescriptionIcon from '@material-ui/icons/Description'
import { useMyVendorCOIs } from '../../hooks/useMyVendorCOIs'
import { useDebounce } from '../../hooks'
import { VendorCOI, VendorCOIStatus } from '../../hooks/useVendorCOIs'
import { getServiceImageDetails } from '../../models'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        padding: theme.spacing(3),
        height: '100%',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(8),
    },
    searchBar: {
        maxWidth: 400,
    },
    tableWrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 450px)',
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    propertyCell: {
        fontWeight: 500,
    },
    serviceIconsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        overflow: 'visible',
    },
    serviceIcon: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        cursor: 'pointer',
        flexShrink: 0,
        objectFit: 'contain',
    },
    serviceColorDot: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        flexShrink: 0,
    },
    moreServicesChip: {
        fontSize: '11px',
        height: 24,
        backgroundColor: theme.palette.grey[200],
        cursor: 'pointer',
    },
    statusChip: {
        fontSize: '11px',
        height: 24,
        '& .MuiChip-icon': {
            fontSize: '14px',
        },
    },
    compliantChip: {
        backgroundColor: '#dcfce7',
        color: '#166534',
        borderColor: '#bbf7d0',
    },
    nonCompliantChip: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        borderColor: '#fecaca',
    },
    expiredChip: {
        backgroundColor: '#f3f4f6',
        color: '#6b7280',
        borderColor: '#d1d5db',
    },
    expiringChip: {
        backgroundColor: '#fef9c3',
        color: '#854d0e',
        borderColor: '#fef08a',
    },
    notYetEffectiveChip: {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        borderColor: '#93c5fd',
    },
    pendingChip: {
        backgroundColor: '#f3f4f6',
        color: '#6b7280',
        borderColor: '#d1d5db',
    },
    emptyContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8),
        gap: theme.spacing(2),
    },
    emptyIcon: {
        fontSize: 48,
        color: theme.palette.grey[400],
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
        minWidth: 200,
        maxWidth: 350,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    coverageCell: {
        fontSize: '12px',
        color: theme.palette.text.secondary,
        maxWidth: 180,
    },
    // View modal styles
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    modalContent: {
        padding: 0,
        display: 'flex',
        height: 'calc(85vh - 80px)',
    },
    documentViewer: {
        flex: 1,
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    detailsPanel: {
        width: 450,
        borderLeft: `1px solid ${theme.palette.divider}`,
        overflowY: 'auto',
        padding: theme.spacing(3),
    },
    detailSection: {
        marginBottom: theme.spacing(3),
    },
    detailSectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1.5),
        color: theme.palette.text.primary,
    },
    detailRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(0.75, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    detailLabel: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    detailValue: {
        fontWeight: 500,
        fontSize: '13px',
        textAlign: 'right',
    },
    aiSummaryCard: {
        backgroundColor: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
    },
    aiSummaryText: {
        fontSize: '13px',
        color: '#0369a1',
        lineHeight: 1.6,
    },
    complianceIssues: {
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
    complianceIssueText: {
        fontSize: '12px',
        color: '#991b1b',
        whiteSpace: 'pre-wrap',
    },
}))

const formatDate = (dateString: string | null) => {
    if (!dateString) return '—'
    const [datePart] = dateString.split('T')
    const [year, month, day] = datePart.split('-')
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ]
    return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`
}

const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '—'
    const num =
        typeof value === 'string'
            ? parseFloat(value.replace(/[^0-9.-]/g, ''))
            : value
    if (isNaN(num)) return '—'
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num)
}

export const VendorMyCOIs: React.FC = () => {
    const classes = useStyles()

    const { myCOIs, isLoadingMyCOIs } = useMyVendorCOIs()

    const [searchTerm, setSearchTerm] = useState('')
    const debouncedSearchTerm = useDebounce(searchTerm, 200)

    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [selectedCOI, setSelectedCOI] = useState<VendorCOI | null>(null)

    const [failedIcons, setFailedIcons] = useState<Set<number>>(new Set())

    const filteredCOIs = useMemo(() => {
        if (!debouncedSearchTerm.trim()) return myCOIs
        const search = debouncedSearchTerm.toLowerCase()
        return myCOIs.filter(
            (coi) =>
                coi.apartment_name?.toLowerCase().includes(search) ||
                coi.name?.toLowerCase().includes(search) ||
                coi.services?.some((s) =>
                    s.name?.toLowerCase().includes(search),
                ) ||
                coi.ai_summary?.toLowerCase().includes(search),
        )
    }, [myCOIs, debouncedSearchTerm])

    const handleView = useCallback((coi: VendorCOI) => {
        setSelectedCOI(coi)
        setViewModalOpen(true)
    }, [])

    const handleCloseModal = useCallback(() => {
        setViewModalOpen(false)
        setSelectedCOI(null)
    }, [])

    const handleIconError = useCallback((serviceId: number) => {
        setFailedIcons((prev) => new Set(prev).add(serviceId))
    }, [])

    const getStatusChipClass = (status: VendorCOIStatus) => {
        switch (status) {
            case 'COMPLIANT':
                return classes.compliantChip
            case 'NON_COMPLIANT':
                return classes.nonCompliantChip
            case 'EXPIRED':
                return classes.expiredChip
            case 'EXPIRING_SOON':
                return classes.expiringChip
            case 'NOT_YET_EFFECTIVE':
                return classes.notYetEffectiveChip
            case 'PENDING_REVIEW':
            default:
                return classes.pendingChip
        }
    }

    const getStatusIcon = (
        status: VendorCOIStatus,
    ): React.ReactElement | undefined => {
        switch (status) {
            case 'COMPLIANT':
                return <VerifiedUserIcon />
            case 'NON_COMPLIANT':
                return <ReportProblemIcon />
            case 'EXPIRED':
                return <ReportProblemIcon />
            case 'EXPIRING_SOON':
                return <WarningIcon />
            case 'NOT_YET_EFFECTIVE':
                return <ScheduleIcon />
            case 'PENDING_REVIEW':
            default:
                return <HourglassEmptyIcon />
        }
    }

    const getStatusLabel = (status: VendorCOIStatus) => {
        switch (status) {
            case 'COMPLIANT':
                return 'Compliant'
            case 'NON_COMPLIANT':
                return 'Non-Compliant'
            case 'EXPIRED':
                return 'Expired'
            case 'EXPIRING_SOON':
                return 'Expiring Soon'
            case 'NOT_YET_EFFECTIVE':
                return 'Not Yet Effective'
            case 'PENDING_REVIEW':
            default:
                return 'Pending Review'
        }
    }

    const renderServiceIcons = useCallback(
        (coi: VendorCOI) => {
            if (!coi.services || coi.services.length === 0) {
                return (
                    <Typography variant="body2" color="textSecondary">
                        —
                    </Typography>
                )
            }

            const visibleServices = coi.services.slice(0, 4)
            const remainingCount = coi.services.length - 4

            return (
                <Box className={classes.serviceIconsContainer}>
                    {visibleServices.map((service) => {
                        const serviceDetails = getServiceImageDetails(
                            service.id,
                            service.service_category,
                        )
                        const hasValidIcon =
                            serviceDetails?.icon && !failedIcons.has(service.id)

                        return (
                            <Tooltip key={service.id} title={service.name}>
                                {hasValidIcon ? (
                                    <img
                                        src={serviceDetails.icon}
                                        alt={service.name}
                                        className={classes.serviceIcon}
                                        onError={() =>
                                            handleIconError(service.id)
                                        }
                                    />
                                ) : (
                                    <Box
                                        className={classes.serviceColorDot}
                                        style={{
                                            backgroundColor: service.color,
                                        }}
                                    />
                                )}
                            </Tooltip>
                        )
                    })}
                    {remainingCount > 0 && (
                        <Tooltip
                            title={coi.services
                                .slice(4)
                                .map((s) => s.name)
                                .join(', ')}
                        >
                            <Chip
                                size="small"
                                label={`+${remainingCount}`}
                                className={classes.moreServicesChip}
                            />
                        </Tooltip>
                    )}
                </Box>
            )
        },
        [classes, failedIcons, handleIconError],
    )

    const renderCoverageTooltip = useCallback(
        (coi: VendorCOI) => {
            const coverageItems = [
                { label: 'GL Each Occurrence', value: coi.gl_each_occurrence },
                {
                    label: 'GL General Aggregate',
                    value: coi.gl_general_aggregate,
                },
                {
                    label: 'Auto Combined Single Limit',
                    value: coi.auto_combined_single_limit,
                },
                { label: 'WC Each Accident', value: coi.wc_el_each_accident },
                {
                    label: 'Umbrella Each Occurrence',
                    value: coi.umbrella_each_occurrence,
                },
            ].filter((item) => item.value)

            const primaryCoverage = coi.gl_each_occurrence
                ? formatCurrency(coi.gl_each_occurrence)
                : '—'

            if (coverageItems.length === 0) {
                return (
                    <Typography variant="body2" color="textSecondary">
                        —
                    </Typography>
                )
            }

            return (
                <Tooltip
                    title={
                        <Box style={{ padding: 8 }}>
                            <Typography
                                variant="subtitle2"
                                style={{ marginBottom: 8, fontWeight: 600 }}
                            >
                                Coverage Details
                            </Typography>
                            {coverageItems.map((item, idx) => (
                                <Box
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: 4,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        style={{ marginRight: 16 }}
                                    >
                                        {item.label}:
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {formatCurrency(item.value)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    }
                >
                    <Box
                        className={classes.coverageCell}
                        style={{ cursor: 'pointer' }}
                    >
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {primaryCoverage}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            GL Each Occurrence
                        </Typography>
                    </Box>
                </Tooltip>
            )
        },
        [classes],
    )

    const isPdf = useMemo(() => {
        return (
            selectedCOI?.file?.toLowerCase().endsWith('.pdf') ||
            selectedCOI?.file?.includes('/pdf') ||
            selectedCOI?.name?.toLowerCase().endsWith('.pdf')
        )
    }, [selectedCOI])

    const viewerUrl = useMemo(() => {
        if (!selectedCOI?.file) return ''
        if (isPdf) {
            return `https://docs.google.com/viewer?url=${encodeURIComponent(
                selectedCOI.file,
            )}&embedded=true`
        }
        return selectedCOI.file
    }, [selectedCOI, isPdf])

    if (isLoadingMyCOIs) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography variant="h6">
                    My Certificates of Insurance
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    View all certificates of insurance you have submitted to
                    properties.
                </Typography>
            </Box>

            <TextField
                className={classes.searchBar}
                variant="outlined"
                size="small"
                placeholder="Search by property, service, or document..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
                fullWidth
            />

            {filteredCOIs.length === 0 ? (
                <Box className={classes.emptyContainer}>
                    <DescriptionIcon className={classes.emptyIcon} />
                    <Typography variant="h6" color="textSecondary">
                        No Certificates Found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {myCOIs.length === 0
                            ? 'You have not submitted any certificates of insurance yet.'
                            : 'No certificates match your search criteria.'}
                    </Typography>
                </Box>
            ) : (
                <Paper className={classes.tableWrapper}>
                    <TableContainer className={classes.tableContainer}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow className={classes.headerRow}>
                                    <TableCell className={classes.headerCell}>
                                        Property
                                    </TableCell>
                                    <TableCell className={classes.headerCell}>
                                        Service
                                    </TableCell>
                                    <TableCell className={classes.headerCell}>
                                        Effective
                                    </TableCell>
                                    <TableCell className={classes.headerCell}>
                                        Expiration
                                    </TableCell>
                                    <TableCell className={classes.headerCell}>
                                        Coverage
                                    </TableCell>
                                    <TableCell className={classes.headerCell}>
                                        Status
                                    </TableCell>
                                    <TableCell className={classes.headerCell}>
                                        AI Summary
                                    </TableCell>
                                    <TableCell
                                        className={classes.headerCell}
                                        align="right"
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCOIs.map((coi) => (
                                    <TableRow
                                        key={coi.id}
                                        className={classes.row}
                                    >
                                        <TableCell
                                            className={classes.propertyCell}
                                        >
                                            {coi.apartment_name || '—'}
                                        </TableCell>
                                        <TableCell>
                                            {renderServiceIcons(coi)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(coi.effective_date)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(coi.expiration_date)}
                                        </TableCell>
                                        <TableCell>
                                            {renderCoverageTooltip(coi)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip
                                                title={
                                                    coi.status ===
                                                        'NON_COMPLIANT' &&
                                                    coi.compliance_notes
                                                        ? coi.compliance_notes
                                                        : ''
                                                }
                                            >
                                                <Chip
                                                    size="small"
                                                    icon={getStatusIcon(
                                                        coi.status,
                                                    )}
                                                    label={getStatusLabel(
                                                        coi.status,
                                                    )}
                                                    className={`${
                                                        classes.statusChip
                                                    } ${getStatusChipClass(
                                                        coi.status,
                                                    )}`}
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell
                                            className={classes.summaryCell}
                                        >
                                            <Tooltip
                                                title={coi.ai_summary || ''}
                                            >
                                                <span>
                                                    {coi.ai_summary || '—'}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View Document">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleView(coi)
                                                    }
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* View Modal */}
            <Dialog
                open={viewModalOpen}
                onClose={handleCloseModal}
                maxWidth={false}
                PaperProps={{
                    style: {
                        width: '90vw',
                        maxWidth: 1400,
                        height: '85vh',
                    },
                }}
            >
                <Box className={classes.modalHeader}>
                    <Box>
                        <Typography variant="h6">
                            {selectedCOI?.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {selectedCOI?.apartment_name}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                        {selectedCOI && (
                            <Chip
                                size="small"
                                icon={getStatusIcon(selectedCOI.status)}
                                label={getStatusLabel(selectedCOI.status)}
                                className={`${
                                    classes.statusChip
                                } ${getStatusChipClass(selectedCOI.status)}`}
                                variant="outlined"
                            />
                        )}
                        <IconButton onClick={handleCloseModal}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <DialogContent className={classes.modalContent}>
                    <Box className={classes.documentViewer}>
                        {selectedCOI?.file && (
                            <iframe
                                src={viewerUrl}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                }}
                                title="Document Viewer"
                                loading="lazy"
                            />
                        )}
                    </Box>
                    <Box className={classes.detailsPanel}>
                        {/* AI Summary */}
                        {selectedCOI?.ai_summary && (
                            <Box className={classes.detailSection}>
                                <Typography
                                    className={classes.detailSectionTitle}
                                >
                                    AI Summary
                                </Typography>
                                <Box className={classes.aiSummaryCard}>
                                    <Typography
                                        className={classes.aiSummaryText}
                                    >
                                        {selectedCOI.ai_summary}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Compliance Notes */}
                        {selectedCOI?.status === 'NON_COMPLIANT' &&
                            selectedCOI?.compliance_notes && (
                                <Box className={classes.detailSection}>
                                    <Typography
                                        className={classes.detailSectionTitle}
                                    >
                                        Compliance Issues
                                    </Typography>
                                    <Box className={classes.complianceIssues}>
                                        <Typography
                                            className={
                                                classes.complianceIssueText
                                            }
                                        >
                                            {selectedCOI.compliance_notes}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                        {/* Certificate Details */}
                        <Box className={classes.detailSection}>
                            <Typography className={classes.detailSectionTitle}>
                                Certificate Details
                            </Typography>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Certificate Holder
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {selectedCOI?.certificate_holder_name ||
                                        '—'}
                                </Typography>
                            </Box>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Effective Date
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatDate(
                                        selectedCOI?.effective_date || null,
                                    )}
                                </Typography>
                            </Box>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Expiration Date
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatDate(
                                        selectedCOI?.expiration_date || null,
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        {/* General Liability */}
                        <Box className={classes.detailSection}>
                            <Typography className={classes.detailSectionTitle}>
                                General Liability
                            </Typography>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Each Occurrence
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatCurrency(
                                        selectedCOI?.gl_each_occurrence,
                                    )}
                                </Typography>
                            </Box>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    General Aggregate
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatCurrency(
                                        selectedCOI?.gl_general_aggregate,
                                    )}
                                </Typography>
                            </Box>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Products/Comp Ops Agg
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatCurrency(
                                        selectedCOI?.gl_products_comp_op_agg,
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Auto Liability */}
                        <Box className={classes.detailSection}>
                            <Typography className={classes.detailSectionTitle}>
                                Auto Liability
                            </Typography>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Combined Single Limit
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatCurrency(
                                        selectedCOI?.auto_combined_single_limit,
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Workers Compensation */}
                        <Box className={classes.detailSection}>
                            <Typography className={classes.detailSectionTitle}>
                                Workers Compensation
                            </Typography>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Each Accident
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatCurrency(
                                        selectedCOI?.wc_el_each_accident,
                                    )}
                                </Typography>
                            </Box>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Disease - Ea Employee
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatCurrency(
                                        selectedCOI?.wc_el_disease_ea_employee,
                                    )}
                                </Typography>
                            </Box>
                            <Box className={classes.detailRow}>
                                <Typography className={classes.detailLabel}>
                                    Disease - Policy Limit
                                </Typography>
                                <Typography className={classes.detailValue}>
                                    {formatCurrency(
                                        selectedCOI?.wc_el_disease_policy_limit,
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Umbrella */}
                        {(selectedCOI?.umbrella_each_occurrence ||
                            selectedCOI?.umbrella_aggregate) && (
                            <Box className={classes.detailSection}>
                                <Typography
                                    className={classes.detailSectionTitle}
                                >
                                    Umbrella Liability
                                </Typography>
                                <Box className={classes.detailRow}>
                                    <Typography className={classes.detailLabel}>
                                        Each Occurrence
                                    </Typography>
                                    <Typography className={classes.detailValue}>
                                        {formatCurrency(
                                            selectedCOI?.umbrella_each_occurrence,
                                        )}
                                    </Typography>
                                </Box>
                                <Box className={classes.detailRow}>
                                    <Typography className={classes.detailLabel}>
                                        Aggregate
                                    </Typography>
                                    <Typography className={classes.detailValue}>
                                        {formatCurrency(
                                            selectedCOI?.umbrella_aggregate,
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    )
}
